import CheckoutBasketItem from "../../Checkout/CheckoutBasketItem.js";
import PosCustomerDetailsCollectionMode from "./PosCustomerDetailsCollectionMode.js";
import PosCustomerDetailsType from "./PosCustomerDetailsType.js";

/**
 * Point of Sale - Customer details service
 *
 * Facilitates logical interactions with customer details data in PoS.
 */
class PosCustomerDetailsService {

	/**
	 * Resolve the allowed customer details inputs for a set of basket items.
	 *
	 * @param {Array<CheckoutBasketItem>} items
	 * @return {Object} Customer details type => customer details collection mode
	 */
	static resolveItemsAllowedCustomerDetails(items, giftAidEnabled) {

		/**
		 * Allowed customer details fields
		 */
		const allowedCustomerDetails = {};

		/**
		 * Iterate the items to resolve details fields to allow
		 */
		for (const i of (items || [])) {
			for (const detailsType of Object.keys(PosCustomerDetailsType)) {

				const typeValue = i.Item?.Session?.Product?.[`CollectCustomer${detailsType}`];

				/** second condition is so required always overrides optional! */
				if ((typeValue !== undefined) &&
					(typeValue !== PosCustomerDetailsCollectionMode.NotApplicable) &&
					(allowedCustomerDetails[PosCustomerDetailsType[detailsType]] !== PosCustomerDetailsCollectionMode.Required)) {

					allowedCustomerDetails[PosCustomerDetailsType[detailsType]] = typeValue;
				}

			}
		}

		/** 
		 * Or basket has Gift-aidable items, and Gift Aid is turned on
		 */
		if (giftAidEnabled && !!items.find(i => CheckoutBasketItem.isGiftAidable(i))) {

			/** "Required" should be determined clientside, based on Gift Aid = Yes */
			allowedCustomerDetails[PosCustomerDetailsType.Name] = PosCustomerDetailsCollectionMode.Optional;
			allowedCustomerDetails[PosCustomerDetailsType.Address] = PosCustomerDetailsCollectionMode.Optional;

			allowedCustomerDetails[PosCustomerDetailsType.GiftAidDeclaration] = PosCustomerDetailsCollectionMode.Required;

		}

		return allowedCustomerDetails;

	}


	/**
	 * Resolve the customer details to submit at checkout for a set of basket items.
	 *
	 * Prunes out customer details that aren't relevant to the items.
	 *
	 * @param {Array<CheckoutBasketItem>} items
	 * @param {Object} answers `Fname`/`Sname`/`Email`/`Phone`/`Address` etc.
	 * @return {Object} `answers` pruned as appropriate for `items`
	 */
	static resolveItemsAllowedCustomerDetailsAnswers(items, answers) {

		const allowedDetails = Object.keys(this.resolveItemsAllowedCustomerDetails(items)).map(i => parseInt(i));

		const resolvedAnswers = {...answers};

		/** Remove disallowed answer fields */

		/**
		 * The `Object.values()` is here as when address input is optional, we could have an empty address object, or one with only Country set (auto-populated), etc.
		 */
		if (answers.Address && (!allowedDetails.includes(PosCustomerDetailsType.Address) || !Object.values(answers.Address).every(a => a))) {
			delete resolvedAnswers.Address;
		}
		if (answers.Email && !allowedDetails.includes(PosCustomerDetailsType.Email)) {
			delete resolvedAnswers.Email;
		}
		if ((answers.Fname || answers.Sname) && !allowedDetails.includes(PosCustomerDetailsType.Name)) {
			delete resolvedAnswers.Fname;
			delete resolvedAnswers.Sname;
		}
		if (answers.Phone && !allowedDetails.includes(PosCustomerDetailsType.Phone)) {
			delete resolvedAnswers.Phone;
		}

		return resolvedAnswers;

	}

}

export default PosCustomerDetailsService;
